/**
 * Location detail → Activity tab. Audit log (`/locations/:id/logs`) rendered as a
 * `Timeline` — one entry per action (actor + description + timestamp). Read-only.
 */
import { Activity } from 'lucide-react';
import { Timeline, EmptyState, type TimelineItem } from '@vnetwork/ui/data';
import { useEntityLogs, type WarehouseActivityLog } from '@/api/capacities';
import { formatDateTime } from '@/lib/format';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

const logTitle = (log: WarehouseActivityLog): string =>
  log.action || log.description || 'Activity';

export function LocationActivityTab({ id }: { id: string }) {
  const { data, isLoading, isError, refetch } = useEntityLogs('locations', id);

  if (isError) return <TabError title="Failed to load activity" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading rows={4} />;

  const logs = data?.results ?? [];
  if (logs.length === 0) {
    return (
      <EmptyState
        icon={<Activity size={24} />}
        title="No activity"
        description="No recorded actions for this location yet."
      />
    );
  }

  const items: TimelineItem[] = logs.map((log, i) => ({
    id: i,
    title: logTitle(log),
    meta: log.employee_name || undefined,
    description: log.action && log.description ? log.description : undefined,
    timestamp: log.created_at ? formatDateTime(log.created_at) : undefined,
    tone: 'neutral',
  }));

  return <Timeline items={items} />;
}
